import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import WebsiteLayout from "../layouts/WebsiteLayout";
import BackNavButton from "../components/common/BackNavButton";
import { reverseGeocodeGoogle, searchGoogleAddresses } from "../utils/googleLocationService";
import { getSavedHomeLocation, saveHomeLocation } from "../utils/homeLocationStorage";
import "./HomePage.css";

function shortenAddress(label) {
  const value = String(label || "").trim();
  if (!value) return "";
  return value.length > 58 ? `${value.slice(0, 58)}...` : value;
}

export default function SelectLocationPage() {
  const navigate = useNavigate();
  const initial = useMemo(() => getSavedHomeLocation(), []);
  const [query, setQuery] = useState(initial.label);
  const [results, setResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [resolvingCurrent, setResolvingCurrent] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setResults([]);
      setSearchError("");
      return;
    }
    let alive = true;
    const timer = window.setTimeout(async () => {
      setLoadingSearch(true);
      setSearchError("");
      try {
        const mapped = await searchGoogleAddresses(q);
        if (!alive) return;
        setResults(mapped);
      } catch {
        if (alive) setSearchError("Unable to fetch addresses");
      } finally {
        if (alive) setLoadingSearch(false);
      }
    }, 350);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [query]);

  const onSelectAddress = (item) => {
    saveHomeLocation(item);
    navigate("/", { replace: true });
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setSearchError("Location is not supported on this device");
      return;
    }
    setResolvingCurrent(true);
    setSearchError("");
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = String(position?.coords?.latitude || "").trim();
        const long = String(position?.coords?.longitude || "").trim();
        if (!lat || !long) {
          setResolvingCurrent(false);
          setSearchError("Unable to read current location");
          return;
        }
        try {
          const label =
            (await reverseGeocodeGoogle({ lat, long })) ||
            initial.label ||
            `Lat ${Number(lat).toFixed(4)}, Long ${Number(long).toFixed(4)}`;
          saveHomeLocation({ lat, long, label });
          navigate("/", { replace: true });
        } catch {
          saveHomeLocation({
            lat,
            long,
            label: initial.label || `Lat ${Number(lat).toFixed(4)}, Long ${Number(long).toFixed(4)}`,
          });
          navigate("/", { replace: true });
        } finally {
          setResolvingCurrent(false);
        }
      },
      () => {
        setResolvingCurrent(false);
        setSearchError("Please allow location permission");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 120000 }
    );
  };

  return (
    <WebsiteLayout>
      <section className="home-page-wrap">
        <div className="home-page-inner">
          <div className="nearby-hosts-top">
            <BackNavButton to="/" ariaLabel="Back to home" />
            <h1 className="nearby-hosts-title">Select Address</h1>
            <span className="nearby-hosts-top-spacer" aria-hidden />
          </div>

          <div className="location-picker-subtitle">Change Your Location</div>
          <input
            type="text"
            className="home-page-search-input"
            placeholder="Search city, area, address"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          <button
            type="button"
            className="location-picker-current-btn"
            onClick={useCurrentLocation}
            disabled={resolvingCurrent}
          >
            {resolvingCurrent ? "Detecting current location..." : "Use my current location"}
          </button>

          {searchError ? <p className="location-picker-error">{searchError}</p> : null}
          {loadingSearch ? <p className="location-picker-note">Searching addresses...</p> : null}

          <div className="location-picker-list">
            {results.map((item) => (
              <button
                key={`${item.lat}:${item.long}:${item.label}`}
                type="button"
                className="location-picker-item"
                onClick={() => onSelectAddress(item)}
                title={item.label}
              >
                {shortenAddress(item.label)}
              </button>
            ))}
          </div>
        </div>
      </section>
    </WebsiteLayout>
  );
}
